import React, { useEffect, useState } from 'react';
import { FaPlane, FaSave, FaTimes, FaTrash, FaUndo } from 'react-icons/fa';
import FormTrecho from './FormTrecho';
import {
    CAMPOS_GRUPO, aberturasDaTela, baseDoVinculo, pontasDoVinculo, resumoDoTrecho, rotaDoTrecho,
    trechoEfetivo, trechoParaTela, trechoParaGravar,
} from '../util/malhaAereo';
import '../css/financeiro-parcela-pagar.css';

// A origem se edita como um trecho dela mesma. O seletor mostra a própria origem, travado, e o campo
// Origem é só o que a edição muda: vazio vale a origem; com o badge ligado o texto detalha a origem
// ("Belo Horizonte (Confins)"), e desligado troca. A origem não tem `origemPai` no back, então o lugar
// dela é gravado já composto no próprio texto, e o formulário o separa de novo ao abrir.
const origemBase = (item) => String(item?.valoresDoTrecho?.origem ?? item?.original?.origem ?? item?.origem ?? '').trim();
const telaDaOrigem = (item) => {
    const base = origemBase(item);
    const efetivo = String(item?.origem ?? '').trim();
    const detalhe = base && efetivo.startsWith(`${base} (`) && efetivo.endsWith(')') ? efetivo.slice(base.length + 2, -1) : null;
    const [origem, origemHerdada] = !efetivo || efetivo === base ? ['', true]
        : (detalhe != null ? [detalhe, true] : [efetivo, false]);
    const tela = { ...trechoParaTela(item), origem_id: String(item.id), origem, origemHerdada };
    // Os campos abrem pelo texto que a tela mostra: o `trechoParaTela` lê o lugar da própria origem
    // como texto digitado, e abriria a ida vazia ao lado de uma volta própria.
    return { ...tela, ...aberturasDaTela(tela) };
};
const origemGravada = (item, gravar) => {
    const base = origemBase(item);
    const texto = String(gravar.origem ?? '').trim();
    if (!texto || texto.toLowerCase() === base.toLowerCase()) return base;
    return gravar.origemHerdada ? `${base} (${texto})` : texto;
};

/**
 * Edição de um vínculo da malha. O MESMO modal serve a todos os tipos — o que muda
 * é só o texto e a ação destrutiva:
 *
 *   'trecho' / 'origem' → editar cria a sobrescrita (o pedido da solicitação fica intacto)
 *   'editada'           → editar altera a sobrescrita; Reverter volta ao original
 *   'grupo'             → editar altera o grupo; Excluir apaga e solta as opções
 *
 * A ORIGEM se edita como o trecho, com as mesmas regras (observação, passageiros com o teto do que
 * o RSVP pediu, Reverter), e com o seletor de origem travado nela mesma (`telaDaOrigem`).
 *
 * O ESTADO é um trecho, igual ao da solicitação: valores e marcas de tela no mesmo objeto,
 * montado pelo `trechoParaTela` e gravado pelo `trechoParaGravar`. Enquanto ele era montado aqui
 * à mão, divergia em silêncio — as direções abriam sempre e os badges de herança nasciam
 * desligados, ao contrário da regra.
 *
 * A DIREÇÃO (o "Sem trecho") só o grupo avulso define: no trecho ela é do RSVP, e o back grava
 * vazio na sobrescrita de todo jeito. Passageiros o Aéreo ajusta nos dois, e no trecho o número
 * vale só nesta malha.
 *
 * Nada aqui vai ao banco: o modal só mexe no state da tela, e a gravação acontece
 * no Salvar da Importação.
 *
 * @param {Object}   item      vínculo selecionado (já com os valores efetivos)
 * @param {Function} onSalvar  (campos) => void
 * @param {Function} onRemover () => void — reverter (editada) ou excluir (grupo)
 */
export default function GrupoMalhaModal({ aberto, item, origens = [], pedido = null,
    onFechar, onSalvar, onRemover }) {
    // UM estado, o trecho inteiro — valores e marcas juntos, como na solicitação.
    const [trecho, setTrecho] = useState(null);
    const [salvando, setSalvando] = useState(false);

    // Reabre sempre com os valores do item selecionado (nunca com o anterior), e pelo MESMO caminho
    // da solicitação: o vínculo vira estado de tela e pronto. Este modal só expõe o state — o que
    // nele é sobrescrita de verdade quem decide é a página da malha, no Salvar dela, que é quem
    // conhece os valores do trecho por baixo.
    useEffect(() => {
        if (aberto && item) setTrecho(baseDoVinculo(item) === 'origem' ? telaDaOrigem(item) : trechoParaTela(item));
    }, [aberto, item]);

    if (!aberto || !item || !trecho) return null;

    const tipo = item.tipo ?? 'trecho';
    const ehGrupo = tipo === 'grupo';
    const ehOrigem = baseDoVinculo(item) === 'origem';
    // Sem `onRemover` não há ação destrutiva: é o caso do grupo que ainda não existe — nada a
    // excluir, e o botão chamaria uma função que não veio.
    const podeRemover = !!onRemover && (ehGrupo || tipo === 'editada');
    // Origem sem trecho ou trecho: o que a edição sobrescreve, nesta malha.

    const set = (campo, valor) => setTrecho(prev => ({ ...prev, [campo]: valor }));

    // Há sobrescrita de observação quando o Aéreo escreveu algo diferente do texto do RSVP. É o que
    // acende o (i) ao lado do rótulo: sem sobrescrita, o campo vazio já significa "vale a do
    // pedido", e o (i) abriria justamente o que está valendo.
    const limpo = (v) => String(v ?? '').trim();
    const obsSobrescrita = !ehGrupo && limpo(trecho.observacao) !== ''
        && limpo(trecho.observacao) !== limpo(item.observacaoTrecho);

    const executar = (acao) => {
        setSalvando(true);
        Promise.resolve(acao()).finally(() => setSalvando(false));
    };

    // A referência da herança, do formulário e do cabeçalho. A origem sai da ESCOLHIDA AGORA, não da
    // que o back conhecia: o grupo novo ainda não existe lá, e trocar a origem no select precisa
    // acender o badge na hora. O `item` fica de reserva, para o caso de a origem não estar na lista. O
    // destino é o do pedido, que vale para trecho e grupo igual.
    const referencias = {
        origemPai: ehOrigem ? origemBase(item)
            : (origens.find(o => String(o.chave) === String(trecho.origem_id))?.nome ?? item.origemPai ?? ''),
        destino: pedido?.destino ?? item.destinoPedido ?? '',
    };
    // As datas do pedido, pelo mesmo motivo: o grupo novo não passou pelo back e não tem as dele.
    const heranca = {
        dataIda: pedido?.dataIda ?? item.dataIdaPedido,
        dataRetorno: pedido?.dataRetorno ?? item.dataRetornoPedido,
    };
    // O cabeçalho é o do trecho na tela da solicitação: a rota e a legenda das datas, pelas mesmas
    // funções, e acompanhando o que se edita aqui. Sem lugar nenhum, fica o nome do vínculo.
    const rota = rotaDoTrecho(pontasDoVinculo(trechoEfetivo(trecho), referencias),
        { ida: trecho.ida !== false, retorno: trecho.retorno !== false })
        || (ehGrupo ? 'Grupo' : (ehOrigem ? 'Origem' : 'Trecho'));
    const legenda = resumoDoTrecho(trecho, heranca);

    // Campo de perna fechada grava VAZIO, como na solicitação: o lugar próprio da volta só existe
    // enquanto está à vista, e fechá-lo é dizer que ela volta a espelhar a ida. O texto fica no
    // estado até aqui, para voltar se a perna reabrir antes de salvar.
    // No trecho da solicitação, a direção é do RSVP e volta como veio.
    const salvarCampos = () => {
        // Mesma regra da solicitação, e ela mora num lugar só: o que as marcas de tela escondem
        // grava vazio, e as marcas não vão ao banco.
        const gravar = trechoParaGravar(trecho);
        // SÓ os campos que a malha grava. O vínculo chega do back com metadado junto —
        // `valoresDoTrecho`, `pontas`, `original` — e o estado da tela o carrega adiante. Vazando
        // para o Salvar ele não significa nada ali e ainda quebra: são objetos, e a página compara
        // valor a valor para saber o que virou sobrescrita.
        const campos = CAMPOS_GRUPO.reduce((acc, k) => ({ ...acc, [k]: gravar[k] ?? null }), {});
        // Na origem, o lugar volta composto (`origemGravada`), e o seletor travado não vira edição.
        if (ehOrigem) {
            campos.origem = origemGravada(item, gravar);
            campos.origemHerdada = true;
            campos.origem_id = null;
        }
        onSalvar(campos);
    };

    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg"
            // O `.modal` do Bootstrap carregado (5.0.0-beta1, src/css/bootstrap.css) é overflow:hidden e só
            // rola com `modal-open` no body, que esta tela não põe. Sem isto, o formulário mais alto que a
            // janela era cortado embaixo, e o Salvar junto. Rola o fundo, e não o corpo
            // (modal-dialog-scrollable): o corpo rolável volta a recortar o calendário que o --flow libera
            // (`.modal-dialog-scrollable .modal-content` vence o `--flow` por especificidade).
            style={{ overflowY: 'auto' }}
            onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
            {/* Mais largo que o `modal-lg` (800px), e só aqui: o formulário do trecho tem as quatro
                pontas e os dois blocos de horário, e nos 800 ele quebrava em pontos diferentes dos
                da solicitação. O `100% - 1rem` é o teto em tela estreita — o mesmo respiro que o
                Bootstrap dá ao `.modal-dialog`. */}
            <div className="modal-dialog modal-dialog-centered modal-lg"
                style={{ maxWidth: 'min(100% - 1rem, 1085px)' }}>
                {/* --flow: os date-pickers precisam vazar o card sem corte. */}
                <div className="modal-content fin-pp__modal fin-pp__modal--flow">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaPlane size={16} /></span>
                        <h3 className="fin-pp__modal-title">{rota}
                            <span className="fin-pp__modal-sub">{legenda}</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onFechar}>
                            <FaTimes size={16} />
                        </button>
                    </div>

                    <div className="modal-body" style={{ minHeight: 330 }}>
                        {/* O MESMO formulário da solicitação, e o MESMO estado: um trecho, com as
                            regras de edição vindas do `malhaAereo` — ligar a direção, marcar "Sem
                            trecho", religar o herdado ao apagar o texto. Nada é reimplementado aqui.

                            A referência da herança é o vínculo sendo editado: `origemPai` e
                            `destinoPedido` vêm de `EventoMalhaAerea::vinculos()`. */}
                        <FormTrecho editavel={!salvando} trecho={trecho}
                            referencias={referencias}
                            heranca={heranca}
                            /* A origem a que a linha pertence NESTA malha — a solicitação não muda,
                               e o trecho passa a levar o badge de editada. Vale para os dois: no
                               trecho é repontar, e no grupo avulso é dizer de qual origem ele é (ou
                               nenhuma, que é como ele nasce). Na própria origem, ele aparece travado
                               nela. */
                            origemSolicitacao={ehOrigem
                                ? { opcoes: [{ chave: String(item.id), nome: origemBase(item) }], fixa: true }
                                : { opcoes: origens }}
                            /* O teto é o da origem: o trecho não leva mais gente do que ela pediu, e
                               a regra é a mesma da solicitação. Sem origem, campo livre. */
                            tetoPassageiros={item.quantidadeDaOrigem ?? null}
                            /* Editável, como a quantidade de passageiros: o Aéreo anota o que fez
                               NESTA malha e a solicitação não muda. Vazia vale a do RSVP. O texto
                               original não se perde — vai no (i) ao lado do rótulo. */
                            observacao={{
                                label: ehOrigem ? 'Observação da origem' : 'Observação do trecho',
                                original: obsSobrescrita ? item.observacaoTrecho : null,
                                placeholder: 'Vazio vale a observação da solicitação',
                            }}
                            onTrecho={setTrecho} />
                    </div>

                    <div className="fin-pp__modal-foot">
                        {podeRemover && (
                            <button type="button" disabled={salvando} onClick={() => executar(onRemover)}
                                className="fin-pp__btn fin-pp__btn--danger-ghost" style={{ marginRight: 'auto' }}>
                                {ehGrupo ? <FaTrash size={13} /> : <FaUndo size={13} />}
                                {ehGrupo ? 'Excluir grupo' : (ehOrigem ? 'Reverter origem' : 'Reverter trecho')}
                            </button>
                        )}
                        <button type="button" className="fin-pp__btn" onClick={onFechar} disabled={salvando}>
                            Cancelar
                        </button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary" disabled={salvando}
                            onClick={() => executar(salvarCampos)}>
                            <FaSave size={13} />
                            {salvando ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
